import { DISPLAY_DATE_FORMAT } from 'constants/dates';
import { IExchangeRateExists } from 'api/invoices/invoices.types';
import { addDays, format } from 'date-fns';
import { t } from 'i18next';
import { CurrencyCode, InvoiceClass } from 'pages/invoices/invoices.types';
import { UseFormTrigger } from 'react-hook-form';
import { FormInputs } from './types';

export const currencyCodeValidationRules = (
  exchangeRateDateData?: number,
  exchangeRateExistsData?: IExchangeRateExists,
) => ({
  required: true,
  validate: {
    exchangeRate: (v: CurrencyCode | null) => {
      if (
        exchangeRateDateData &&
        exchangeRateExistsData &&
        v &&
        exchangeRateExistsData[v?.currency]
      ) {
        const exchangeRateDate = format(
          exchangeRateDateData,
          DISPLAY_DATE_FORMAT,
        );
        const exchangeRateExistsDate = format(
          exchangeRateExistsData[v?.currency],
          DISPLAY_DATE_FORMAT,
        );
        if (exchangeRateDate !== exchangeRateExistsDate) {
          return t('exchangeRateAlternateDate', {
            ns: 'invoice',
            replace: {
              requestedDate: exchangeRateDate,
              currency: v.currency,
              foundDate: exchangeRateExistsDate,
            },
          });
        }
        return true;
      }
    },
  },
});

export const issueDateValidationRules = (
  trigger: UseFormTrigger<FormInputs>,
  lastIssueDateData?: number,
  invoiceClassValue?: InvoiceClass | null,
  isEditing?: boolean,
) => ({
  required: t('required', { ns: 'validation' }),
  validate: async (v: string | undefined) => {
    await trigger('saleDate');
    if (v) {
      if (
        lastIssueDateData &&
        new Date(v) < new Date(lastIssueDateData) &&
        !(invoiceClassValue?.code === 'FK' || invoiceClassValue?.code === 'FZ')
      ) {
        return t('notBeforeDependentDate', {
          ns: 'validation',
          replace: {
            '0': format(
              addDays(new Date(lastIssueDateData), -1),
              DISPLAY_DATE_FORMAT,
            ),
          },
        });
      }
      if (new Date(v) > new Date() && !isEditing) {
        return t('notAfterDependentDate', {
          ns: 'validation',
          replace: {
            '0': format(addDays(new Date(), 1), DISPLAY_DATE_FORMAT),
          },
        });
      }
    }
    return true;
  },
});

export const saleDateValidationRules = (issueDateValue?: string) => ({
  required: true,
  validate: (v: string | undefined) => {
    if (v) {
      const currentDate = new Date();
      const selectedDate = new Date(v);
      if (issueDateValue) {
        const issueDate = new Date(issueDateValue);
        if (selectedDate > issueDate) {
          return t('notAfterDependentDate', {
            ns: 'validation',
            replace: {
              '0': format(
                addDays(new Date(issueDateValue), 1),
                DISPLAY_DATE_FORMAT,
              ),
            },
          });
        }
        return true;
      }
      if (selectedDate > currentDate) {
        return t('notAfterDependentDate', {
          ns: 'validation',
          replace: {
            '0': format(addDays(currentDate, 1), DISPLAY_DATE_FORMAT),
          },
        });
      }
      return true;
    }
  },
});

export const dueDateValidationRules = (
  issueDateValue?: string,
  isDueDateCad?: boolean,
) => ({
  required: true,
  validate: (v: string | undefined) => {
    if (!isDueDateCad) {
      if (v && issueDateValue && new Date(v) < new Date(issueDateValue)) {
        return t('notBeforeDependentDate', {
          ns: 'validation',
          replace: {
            '0': format(
              addDays(new Date(issueDateValue), -1),
              DISPLAY_DATE_FORMAT,
            ),
          },
        });
      }
    }
    return true;
  },
});

export const receiptDateValidationRules = (isEditing?: boolean) => ({
  required: true,
  validate: (v: string | undefined) => {
    if (v && new Date(v) > new Date() && !isEditing) {
      return t('notAfterDependentDate', {
        ns: 'validation',
        replace: {
          '0': format(addDays(new Date(), 1), DISPLAY_DATE_FORMAT),
        },
      });
    }
    return true;
  },
});
